import {
  MoreVertical,
  Receipt,
  Trash2,
  Calendar,
  ArrowRight,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { formatCurrency } from "../../lib/currency";
import type { Event } from "../../types";
import { motion } from "framer-motion";

interface EventCardProps {
  event: Event;
  onClick: () => void;
  onDelete: () => void;
}

export function EventCard({ event, onClick, onDelete }: EventCardProps) {
  const totalExpenses = event.expenses.reduce((sum, e) => sum + e.amount, 0);
  const expenseCount = event.expenses.length;

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  const getInitials = (name: string) => {
    return name.slice(0, 2).toUpperCase();
  };

  const participantsToShow = event.participants.slice(0, 4);
  const remainingParticipants = event.participants.length - 4;

  return (
    <motion.div
      whileHover={{ scale: 0.995 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.2 }}
    >
      <Card
        className="group relative overflow-hidden border-sidebar-border/50 bg-card hover:border-primary/50 transition-colors cursor-pointer"
        onClick={onClick}
      >
        <div className="absolute top-0 left-0 w-1 h-full bg-linear-to-b from-primary to-primary/50" />

        <CardContent className="p-5 pl-7">
          <div className="flex justify-between items-start mb-4">
            <div className="space-y-1">
              <h3 className="font-semibold text-lg tracking-tight text-foreground">
                {event.name}
              </h3>
              <div className="flex items-center text-xs text-muted-foreground gap-2">
                <Calendar className="h-3 w-3" />
                <span>
                  {new Date(event.createdAt).toLocaleDateString("es-AR", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
              </div>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger
                asChild
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 -mr-2 text-muted-foreground hover:text-foreground"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={handleDelete}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Eliminar evento
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex items-end justify-between">
            <div className="flex -space-x-2">
              {participantsToShow.map((p) => (
                <div
                  key={p.id}
                  className="h-8 w-8 rounded-full bg-muted border-2 border-card flex items-center justify-center text-[10px] font-medium text-muted-foreground ring-offset-2"
                  title={p.name}
                >
                  {getInitials(p.name)}
                </div>
              ))}
              {remainingParticipants > 0 && (
                <div className="h-8 w-8 rounded-full bg-secondary border-2 border-card flex items-center justify-center text-[10px] font-medium text-secondary-foreground">
                  +{remainingParticipants}
                </div>
              )}
              {event.participants.length === 0 && (
                <span className="text-sm text-muted-foreground flex items-center gap-1 italic">
                  Sin participantes
                </span>
              )}
            </div>

            <div className="text-right">
              <div className="flex items-center justify-end gap-1 text-xs text-muted-foreground mb-1">
                <Receipt className="h-3 w-3" />
                <span>{expenseCount} gastos</span>
              </div>
              <div className="text-2xl font-bold tracking-tight text-primary">
                {formatCurrency(totalExpenses, event.currency)}
              </div>
            </div>
          </div>

          <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-2 group-hover:translate-x-0">
            {/* Optional decorative element or arrow */}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
